import React from "react";
import { compose } from "recompose";
import { Link, withRouter } from "react-router-dom";
import classNames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import ButtonBase from "@material-ui/core/ButtonBase";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Toolbar from "@material-ui/core/Toolbar";
import PDFIcon from "@material-ui/icons/PictureAsPdf";

import withContentFilter from "../hocs/withContentFilter";
import withGeneralTranslations from "../hocs/withGeneralTranslations";
import withPageInfo from "../hocs/withPageInfo";
import withWebToPrint from "../hocs/withWebToPrint";

import Breadcrumbs from "../components/Breadcrumbs";

import { SmallText } from "../styleguide";

import { ArrowLeft, ArrowRight, ContentAll, ContentLinked } from "../components/Icons";

import { HEADER_HEIGHT } from "../layouts/Header";

export const NAVIGATION_HEIGHT = 64;

const styles = (theme) => ({
    toolbarStyle: {
        minHeight: NAVIGATION_HEIGHT,
        height: NAVIGATION_HEIGHT,
        backgroundColor: "#f0f1f2",
        position: "fixed",
        top: HEADER_HEIGHT,
        left: 0,
        right: 0,
        zIndex: 99,
        paddingLeft: 35,
        paddingRight: 35,
    },
    containerStyle: {
        display: "flex",
        flex: "1 0 auto",
        alignItems: "center",
        maxWidth: 1200,
        marginLeft: "auto",
        marginRight: "auto",
        width: "100%",
    },
    navButtonRootStyle: {
        background: "rgba(255, 255, 255, 0.7)",
        color: "#303a44",
        border: "1px solid #acb1b6",
        minWidth: 36,
        width: 36,
        height: 36,
        borderRadius: 4,
        padding: 0,
        marginRight: 10,
    },
    contentButtonRootStyle: {
        minWidth: 32,
        width: 32,
        height: 32,
        padding: 0,
        margin: "0 10px",
        color: "#303c49",
    },
    topicClusterStyle: {
        top: 0,
        background: "transparent",
    },
    w2pTextStyle: {
        margin: "0 30px 0 8px",
    },
    w2pIconButtonStyle: {
        padding: "0 10px",
    },
    w2pButtonStyle: {
        boxShadow: "none",
        fontSize: theme.typography.pxToRem(12),
        fontFamily: "Arial, sans-serif",
        lineHeight: "normal",
        fontWeight: "normal",
        textTransform: "none",
        width: 110,
        height: 38,
        paddingLeft: 8,
        paddingRight: 8,
        marginRight: 10,
        borderWidth: "1px",
        borderStyle: "solid",
        borderRadius: 4,
        backgroundColor: "#e40045",
        borderColor: "#e40045",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#b10036",
            borderColor: "#b10036",
        },
        "&$w2pInactiveButtonStyle": {
            backgroundColor: "#838a92",
            borderColor: "#838a92",
            color: "#d6d8db",
        },
    },
    w2pInactiveButtonStyle: {
        // empty style rule, see reference above
    },
    w2pCancelButtonStyle: {
        backgroundColor: "#fff",
        borderColor: "#303c49",
        color: "#303c49",
        "&:hover": {
            backgroundColor: "#f0f1f2",
            borderColor: "#303c49",
        },
    },
});

const HistoryButtons = ({ history, classes: { navButtonRootStyle } }) => {
    return (
        <div
            style={{
                display: "inline-flex",
                marginRight: 15,
            }}
        >
            <Button classes={{ root: navButtonRootStyle }} onClick={() => history.goBack()}>
                <ArrowLeft size={12} />
            </Button>
            <Button classes={{ root: navButtonRootStyle }} onClick={() => history.goForward()}>
                <ArrowRight size={12} />
            </Button>
        </div>
    );
};
const StyledHistoryButtons = compose(withRouter, withStyles(styles))(HistoryButtons);

const OverviewShotcuts = ({
    pageInfo,
    setContentFilter,
    resetContentFilter,
    match: { params },
    classes: { contentButtonRootStyle },
}) => {
    const vehicleLink = `/vehicle/${params.vehicleSlug}`;

    return (
        <div
            style={{
                display: "inline-flex",
            }}
        >
            <Link to={vehicleLink} onClick={() => setContentFilter(params.vehicleSlug, pageInfo.type, pageInfo.id)}>
                <IconButton classes={{ root: contentButtonRootStyle }}>
                    <ContentLinked />
                </IconButton>
            </Link>
            <Link to={vehicleLink} onClick={() => resetContentFilter()}>
                <IconButton classes={{ root: contentButtonRootStyle }}>
                    <ContentAll />
                </IconButton>
            </Link>
        </div>
    );
};

const StyledOverviewShortcuts = compose(
    withPageInfo,
    withContentFilter,
    withRouter,
    withStyles(styles)
)(OverviewShotcuts);

const WebToPrint = ({
    webToPrintModeActive,
    activateWebToPrintMode,
    endWebToPrintMode,
    readyToPrint,
    generalTranslations,
    classes: { w2pTextStyle, w2pIconButtonStyle, w2pButtonStyle, w2pInactiveButtonStyle, w2pCancelButtonStyle },
}) => (
    <div>
        {webToPrintModeActive ? (
            <div style={{ display: "flex", alignItems: "center" }}>
                <PDFIcon nativeColor="#303c49" />
                <SmallText className={w2pTextStyle}>{generalTranslations("w2p-mode-enabled")}</SmallText>
                <Button
                    variant="outlined"
                    onClick={endWebToPrintMode}
                    classes={{ root: w2pButtonStyle, outlined: w2pCancelButtonStyle }}
                >
                    {generalTranslations("quit")}
                </Button>

                <Button
                    disabled={!readyToPrint}
                    variant="contained"
                    classes={{ root: w2pButtonStyle, disabled: w2pInactiveButtonStyle }}
                    onClick={() => {
                        if (readyToPrint) {
                            window.print();
                        }
                    }}
                >
                    {generalTranslations("generate-pdf")}
                </Button>
            </div>
        ) : (
            <ButtonBase
                disableRipple
                disableTouchRipple
                className={w2pIconButtonStyle}
                onClick={activateWebToPrintMode}
            >
                <PDFIcon nativeColor="#303c49" />
            </ButtonBase>
        )}
    </div>
);

const StyledWebToPrint = compose(withGeneralTranslations, withWebToPrint, withStyles(styles))(WebToPrint);

const Navigation = ({
    enableOverviewShortcuts,
    onlyShowHome,
    classes: { toolbarStyle, containerStyle, topicClusterStyle },
    inTopicCluster,

    enableWebToPrint,
    onPrint,
}) => (
    <Toolbar
        classes={{
            root: classNames(toolbarStyle, inTopicCluster && topicClusterStyle),
        }}
        disableGutters
    >
        <div className={containerStyle}>
            <StyledHistoryButtons />

            <Breadcrumbs onlyShowHome={onlyShowHome} />

            {enableWebToPrint && <StyledWebToPrint onPrint={onPrint} />}
            {enableOverviewShortcuts && <StyledOverviewShortcuts />}
        </div>
    </Toolbar>
);
export default withStyles(styles)(Navigation);



// WEBPACK FOOTER //
// ./src/layouts/Navigation.js